import React, { useMemo } from 'react';
import { TableModal } from './Modal';

// Hub Location data type
export interface HubLocationData {
    hubName: string;
    totalVehicles: number;
    percentageTotal: number;
    activeVehicles: number;
    idleVehicles: number;
    inRepairVehicles: number;
    brokenVehicles: number;
    lostVehicles: number;
}

interface HubLocationModalProps {
    isOpen: boolean;
    onClose: () => void;
    data: HubLocationData[];
    loading?: boolean;
    hasMore?: boolean;
    onLoadMore?: () => void;
}

// Sort field enum for type safety
enum HubSortField {
    HUB_NAME = 'hubName',
    TOTAL_VEHICLE = 'totalVehicles',
    PERCENTAGE_TOTAL = 'percentageTotal',
    ACTIVE = 'activeVehicles',
    IDLE = 'idleVehicles',
    IN_REPAIR = 'inRepairVehicles',
    BROKEN = 'brokenVehicles',
    LOST = 'lostVehicles'
}

const HubLocationModal: React.FC<HubLocationModalProps> = ({
    isOpen,
    onClose,
    data,
    loading = false,
    hasMore = false,
    onLoadMore
}) => {

    // Column configuration
    const columns = useMemo(() => [
        {
            key: HubSortField.HUB_NAME,
            title: 'Hub Name',
            sortable: true,
            render: (value: string) => (
                <span 
                    className="font-jakarta"
                    style={{
                        fontWeight: 500,
                        fontSize: '14px',
                        lineHeight: '20px',
                        letterSpacing: '-0.006em',
                        color: '#171717'
                    }}
                >
                    {value}
                </span>
            )
        },
        {
            key: HubSortField.TOTAL_VEHICLE,
            title: 'Total Vehicle',
            sortable: true,
            render: (value: number) => (
                <span 
                    className="font-jakarta"
                    style={{
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        letterSpacing: '-0.006em',
                        color: '#171717'
                    }}
                >
                    {value.toLocaleString()}
                </span>
            )
        },
        {
            key: HubSortField.PERCENTAGE_TOTAL,
            title: '% of Total Vehicle',
            sortable: true,
            render: (value: number) => (
                <span 
                    className="text-[14px]"
                    style={{ color: '#5C5C5C' }}
                >
                    {parseFloat(value.toFixed(2))}%
                </span>
            )
        },
        {
            key: HubSortField.ACTIVE,
            title: 'Active',
            sortable: true,
            render: (value: number) => (
                <span 
                    className="font-jakarta"
                    style={{
                        fontWeight: 500,
                        fontSize: '14px',
                        lineHeight: '20px',
                        letterSpacing: '-0.006em',
                        color: '#171717'
                    }}
                >
                    {value.toLocaleString()}
                </span>
            )
        },
        {
            key: HubSortField.IDLE,
            title: 'Idle',
            sortable: true,
            render: (value: number) => (
                <span 
                    className="text-[14px]"
                    style={{ color: '#5C5C5C' }}
                >
                    {value.toLocaleString()}
                </span>
            )
        },
        {
            key: HubSortField.IN_REPAIR,
            title: 'In Repair',
            sortable: true,
            render: (value: number) => (
                <span 
                    className="text-[14px]"
                    style={{ color: '#5C5C5C' }}
                >
                    {value.toLocaleString()}
                </span>
            )
        },
        {
            key: HubSortField.BROKEN,
            title: 'Broken',
            sortable: true,
            render: (value: number) => (
                <span 
                    className="text-[14px]"
                    style={{ color: '#5C5C5C' }}
                >
                    {value.toLocaleString()}
                </span>
            )
        },
        {
            key: HubSortField.LOST,
            title: 'Lost',
            sortable: true,
            render: (value: number) => (
                <span 
                    className="text-[14px]"
                    style={{ color: '#5C5C5C' }}
                >
                    {value.toLocaleString()}
                </span>
            )
        }
    ], []);

    // Column order
    const getColumnOrder = () => [
        HubSortField.HUB_NAME,
        HubSortField.TOTAL_VEHICLE,
        HubSortField.PERCENTAGE_TOTAL,
        HubSortField.ACTIVE,
        HubSortField.IDLE,
        HubSortField.IN_REPAIR,
        HubSortField.BROKEN,
        HubSortField.LOST
    ];

    // Sort function
    const handleSort = (data: HubLocationData[], field: string, direction: 'asc' | 'desc') => {
        return [...data].sort((a, b) => {
            const aValue = a[field as keyof HubLocationData];
            const bValue = b[field as keyof HubLocationData];

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return direction === 'asc'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }

            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return direction === 'asc' ? aValue - bValue : bValue - aValue;
            }

            return 0;
        });
    };

    // CSV value getter
    const getCsvValue = (row: HubLocationData, field: string) => {
        const value = row[field as keyof HubLocationData];
        
        if (value === undefined || value === null) {
            return '';
        }
        
        if (typeof value === 'string') {
            return value.includes(',') || value.includes('"') ? `"${value.replace(/"/g, '""')}"` : value;
        }
        
        if (typeof value === 'number') {
            if (field === 'percentageTotal') {
                return `${value}%`;
            }
            return value.toString();
        }
        
        return String(value);
    };

    return (
        <TableModal
            isOpen={isOpen}
            onClose={onClose}
            title="Hub Location Detail"
            modalTitle="Hub Location"
            description="Monitor all vehicle across all registered physical Dash Hubs."
            data={data}
            columns={columns}
            sortConfig={{
                defaultField: HubSortField.TOTAL_VEHICLE,
                defaultDirection: 'desc'
            }}
            csvFileName="hub-location.csv"
            onSort={handleSort}
            getColumnOrder={getColumnOrder}
            getCsvValue={getCsvValue}
            loading={loading}
            hasMore={hasMore}
            onLoadMore={onLoadMore}
        />
    );
};

export default HubLocationModal;
